import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import LoadingIndicator from "../Components/LoadingIndicator";

const LeadsReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSummary, setSelectedSummary] = useState("");
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchContent();
  }, [currentPage, appliedSearch, limit, sortBy, sortOrder]);

  const fetchContent = async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "listContent",
        page: String(currentPage),
        limit: String(limit),
        search: appliedSearch || undefined,
        sortBy,
        sortOrder,
      };
      if (appliedSearch.trim()) payload.search = appliedSearch.trim();

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const data = response.data;
      setRows(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load content";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleSort = (column) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("createdAt");
      setSortOrder("desc");
    }

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            fontSize: "10px",
            opacity: 0.35,
            marginLeft: "5px",
          }}
        ></i>
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    );
  };

  const handleExport = async () => {
    try {
      const payload = {
        action: "excelContent",
        search: appliedSearch || undefined,
      };

      const res = await axios.post(API_URL, payload);

      const base64 = res.data.body;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);

      const link = document.createElement("a");
      link.href = url;
      link.download = `lead-report-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to export lead report");
    }
  };

  const getLeadVisibility = (row) => {
    if (!row.leadVisibility || row.leadVisibility.length === 0) return "-";
    return row.leadVisibility.join(", ");
  };

  const getCategoryLabel = (row) => {
    if (!row.category) return "-";
    if (typeof row.category === "object") return row.category.category || "-";
    return row.category;
  };

  const getRequirementSummary = (row) => {
    const summary = row.requirementSummary || "-";
    if (summary === "-" || summary.length <= 25) return summary;
    return summary.substring(0, 25) + "...";
  };

  const openSummaryModal = (summary) => {
    setSelectedSummary(summary || "-");
    setIsSummaryModalOpen(true);
  };

  const closeSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setSelectedSummary("");
  };

  const tdStyle = { whiteSpace: "nowrap", verticalAlign: "middle" };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", " ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="d-flex align-items-center">
          <span className="mr-2">Show</span>

          <select
            className="form-control"
            style={{ width: "80px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="ml-2 mr-3">Entries</span>

          <span className="pagination-info">
            Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
            {Math.min(currentPage * limit, totalCount)} of {totalCount} entries
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${
                currentPage === page ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fas fa-angle-right"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Content Report</h1>
            <p>View all content entries.</p>
          </div>
          <div className="col-sm-6 text-right">
            <button
              className="btn btn-success"
              onClick={handleExport}
              disabled={isLoading}
            >
              <i className="fas fa-file-excel mr-2"></i> Export
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <div className="row mb-3 align-items-end">
            <div className="col-xl-5 col-lg-6 col-md-8 col-sm-12 mb-2">
              <form onSubmit={handleSearch}>
                <label className="font-weight-bold mb-1">Search</label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search ..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="input-group-append">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      <i className="fas fa-search"></i>
                    </button>

                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>

          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No content data found.</div>
          ) : (
            <>
              <div className="employee-table-wrapper">
                <table className="table table-borderless employee-table subscription-table">
                  <thead style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                    <tr>
                      <th>Sl No.</th>

                      <th
                        onClick={() => handleSort("category")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Category {renderSortIcon("category")}
                      </th>

                      <th
                        onClick={() => handleSort("rfqNo")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        RFQ No {renderSortIcon("rfqNo")}
                      </th>

                      <th
                        onClick={() => handleSort("rfqDate")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        RFQ Date {renderSortIcon("rfqDate")}
                      </th>

                      <th
                        onClick={() => handleSort("rfqDueDateTime")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        RFQ Due Date & Time {renderSortIcon("rfqDueDateTime")}
                      </th>

                      <th>Requirement Summary</th>

                      <th>Support Contact</th>

                      <th>Lead Visibility</th>

                      <th
                        onClick={() => handleSort("status")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Status {renderSortIcon("status")}
                      </th>

                      <th
                        onClick={() => handleSort("createdAt")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Created On {renderSortIcon("createdAt")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row._id || idx}>
                        <td style={tdStyle}>
                          {(currentPage - 1) * limit + idx + 1}
                        </td>
                        <td style={tdStyle}>{getCategoryLabel(row)}</td>
                        <td style={tdStyle}>{row.rfqNo || "-"}</td>
                        <td style={tdStyle}>{formatDateTime(row.rfqDate)}</td>
                        <td style={tdStyle}>
                          {formatDateTime(row.rfqDueDateTime)}
                        </td>
                        <td style={tdStyle}>
                          {row.requirementSummary &&
                          row.requirementSummary.length > 25 ? (
                            <span
                              style={{
                                cursor: "pointer",
                                textDecoration: "none",
                              }}
                              onClick={() =>
                                openSummaryModal(row.requirementSummary)
                              }
                              title="Click to view full summary"
                            >
                              {getRequirementSummary(row)}
                            </span>
                          ) : (
                            <span>{getRequirementSummary(row)}</span>
                          )}
                        </td>
                        <td style={tdStyle}>{row.supportContact || "-"}</td>
                        <td>{getLeadVisibility(row)}</td>
                        <td style={tdStyle}>
                          <span
                            className={`status-badge ${row.status === "active" ? "active" : row.status === "pending" ? "pending" : "inactive"}`}
                          >
                            {row.status || "-"}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>
      {isSummaryModalOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h5>Requirement Summary</h5>
              <button
                type="button"
                className="crop-close"
                onClick={closeSummaryModal}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: "1.6",
                  fontSize: "14px",
                }}
              >
                {selectedSummary}
              </div>
            </div>
            <div className="crop-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeSummaryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsReport;
